import { useContext, useMemo } from 'react';
import { useAuth } from '@screentone/addon-auth-wrapper';
import { Button, IconEdit, Tooltip } from '@screentone/core';

import { AltSummModal } from 'components/altsumm-modal/AltSummModal';
import { useContextMenuActions } from 'contexts/context-menu-actions/useContextMenuActions';
import { SummarianContext } from 'contexts/summarian/SummarianContext';
import { AvailableFeatureFlags } from 'data/generated/graphql';
import { usePublicationSettings } from 'hooks/publication-settings';
import { AllessehContent } from 'hooks/useAllessehContentQuery';
import { useContentConstants } from 'hooks/useContentConstants';
import { useFeatureFlag } from 'hooks/useFeatureFlag';
import { buildCMSUrl, buildLiveCoverageUrl, buildMediaUrl } from 'utils/cms';
import styles from './EditAltSumm.module.scss';
import { isAltSummDisabled } from './EditAltSumm.utils';

interface EditAltSummProps {
  content: AllessehContent;
  viewOnly?: boolean;
}

const summarianNotReadyMessage =
  'Summarian is unavailable. Clicking will open the CMS editor where you can update alt-summ instead.';

export const EditAltSumm = ({ content, viewOnly = false }: EditAltSummProps) => {
  const { isSummarianReady } = useContext(SummarianContext);
  const { currentProperty } = useAuth();
  const { data: contentConstants } = useContentConstants();
  const { data: publicationSettingsResp } = usePublicationSettings();
  const { setActionModalComponent, setIsModalOpen } = useContextMenuActions();
  const showEditAltSummOption = useFeatureFlag(AvailableFeatureFlags.ShowEditAltSummOption, false);
  const canEditAltSummWithinCuration = useFeatureFlag(AvailableFeatureFlags.CanEditAltSummWithinCuration, false);
  const isLiveCoverage = content.data.attributes.product.includes('Live Coverage');
  const isLegacyLiveCoverage = isLiveCoverage && content.data.id.startsWith('lc-'); // Legacy LC content starts with "lc-" id
  const isVideo = content.data.attributes.type === 'video';
  const isAudio = content.data.attributes.type === 'audio';

  const liveCoverageUrl = useMemo(() => {
    const lcAltSumUrl = buildLiveCoverageUrl(
      content.data.attributes.source_url,
      content.data.attributes.associations?.root_id ?? ''
    );
    return content.data.attributes.section_type === 'stack' && lcAltSumUrl ? `${lcAltSumUrl}/settings` : lcAltSumUrl;
  }, [
    content.data.attributes.section_type,
    content.data.attributes.source_url,
    content.data.attributes.associations?.root_id
  ]);

  const newspressUrl = useMemo(
    () =>
      buildCMSUrl(
        content.data.id,
        content.data.attributes.type,
        content.data.attributes.source_url,
        content.data.attributes.product
      ),
    [content.data.id, content.data.attributes.type, content.data.attributes.source_url, content.data.attributes.product]
  );

  const mediaUrl = useMemo(
    () =>
      buildMediaUrl(
        content.data.attributes.source_url,
        content.data.attributes.product,
        content.data.attributes.type as 'audio' | 'video',
        content.data.id
      ),
    [content.data.attributes.source_url, content.data.attributes.product, content.data.attributes.type, content.data.id]
  );

  const handleViewLiveCoverageUrl = () => {
    if (!liveCoverageUrl) return;
    window.open(liveCoverageUrl, '_blank');
  };

  const handleViewNewsPressUrl = () => {
    if (!newspressUrl) return;
    window.open(newspressUrl, '_blank');
  };

  const handleMediaUrl = () => {
    if (!mediaUrl) return;
    window.open(mediaUrl, '_blank');
  };

  const handleEditAltSumm = () => {
    if (isLegacyLiveCoverage) {
      handleViewLiveCoverageUrl();
    } else if (isVideo || isAudio) {
      handleMediaUrl();
    } else if (canEditAltSummWithinCuration && isSummarianReady) {
      // Only open Summarian modal if it's ready
      setIsModalOpen(true);
      setActionModalComponent(<AltSummModal content={content} viewOnly={viewOnly} />);
    } else {
      // Fallback to CMS for all other cases (including Summarian not ready)
      handleViewNewsPressUrl();
    }
  };

  if (!showEditAltSummOption) {
    return null;
  }

  const publicationSettings = publicationSettingsResp?.publicationSetting;
  const isDisabled = isAltSummDisabled(content, currentProperty ?? '', publicationSettings, contentConstants);
  const isAFP = content.data.attributes.product.includes('Agence France Presse');

  // temporary solution for disabling by content type - remove once types are in publication table
  const isTappable = content.data.attributes.product.includes('Interactive Media');
  const isWebstory = content.data.attributes.product.includes('WSJ.com Webstory');

  // Check if we'll fallback to CMS (show tooltip to inform user)
  const requiresSummarian = canEditAltSummWithinCuration && !isLegacyLiveCoverage && !isVideo && !isAudio;
  const willFallbackToCMS = requiresSummarian && !isSummarianReady;

  const EditAltSummButton = (
    <Button
      data-testid="edit-alt-summ-option-button"
      tertiary
      icon={IconEdit}
      onClick={handleEditAltSumm}
      disabled={isAFP}
    >
      Edit Alt-Summ
    </Button>
  );

  if (isAFP) {
    return (
      <Tooltip data-testid="edit-alt-summ-option-tooltip">
        <Tooltip.Content
          data-testid="edit-alt-summ-option-tooltip-content"
          id="altSumModalTooltip"
          position="bottom"
          className={styles.altSumTooltip}
          size="lg"
        >
          Linked item needs to be created for alt summary editing AFP content.
        </Tooltip.Content>
        <Tooltip.Trigger data-testid="edit-alt-summ-option-tooltip-trigger" aria-describedby="altSumModalTooltip">
          {EditAltSummButton}
        </Tooltip.Trigger>
      </Tooltip>
    );
  }

  if (isDisabled || (!liveCoverageUrl && !newspressUrl && !mediaUrl) || isTappable || isWebstory) {
    return null;
  }

  // Show tooltip to inform user they'll be redirected to CMS instead of Summarian modal
  if (willFallbackToCMS) {
    return (
      <Tooltip data-testid="edit-alt-summ-option-tooltip">
        <Tooltip.Content
          data-testid="edit-alt-summ-option-tooltip-content"
          id="altSumModalTooltip"
          position="bottom"
          className={styles.altSumTooltip}
          size="lg"
        >
          {summarianNotReadyMessage}
        </Tooltip.Content>
        <Tooltip.Trigger data-testid="edit-alt-summ-option-tooltip-trigger" aria-describedby="altSumModalTooltip">
          {EditAltSummButton}
        </Tooltip.Trigger>
      </Tooltip>
    );
  }

  return EditAltSummButton;
};
